import { $byId } from "@de-xima/xima-common-js-dom";
import { isEmpty, isInstanceLike, notBlank, notEmpty } from "@de-xima/xima-common-js-lang";
import jQuery from "jquery";

import { FrameBusChildHandler, getInstance as getFramebusHandlerInstance } from "./FramebusHandler.ts";
import { encodeURL, getWidgetByVar } from "./Util.ts";

let RemoteCommandWindow: Window | undefined;

const LoadingPage = "/loading";

const askBeforeExitState = {
  ask: false,
  enabled: true,
  message: "",
};

/**
 * @param location New location (URL).
 */
function doRedirect(location: string | undefined) {
  if (notEmpty(location)) {
    try {
      setTimeout(() => window.location.assign(location), 0);
    } catch {
      console.warn("Error redirecting to URL", location);
    }
  }
}

export function redirect(location: string): void {
  if (notBlank(location)) {
    window.self.location.assign(location);
  } else {
    console.warn("No location for redirection given!");
  }
}

/**
 * Callback function that may be passed to PrimeFaces.monitorDownload(onStartDownload, onStopDownload).
 * Shows the loading dialog.
 */
export function onStartDownload(): void {
  disablePromptBeforeExit();
  const dialog = getWidgetByVar("downloadDialog", PrimeFaces.widget.Dialog);
  const framebus = getFramebusHandlerInstance();
  if (dialog !== undefined) {
    // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
    dialog["initPosition"]();
    dialog.show();
  } else if (isInstanceLike(framebus, FrameBusChildHandler)) {
    framebus.emitDownloadDialog("show");
  }
}

/**
 * Navigates to the first tab of the given tabView with an input field that is invalid.
 * @param widgetVar Name of the widget variable for the `p:tabView`.
 */
export function gotoFirstTabWithError(widgetVar: string): void {
  // Delay, necessary when called from an oncomplete callback.
  jQuery(() => {
    const tab = getWidgetByVar(widgetVar, PrimeFaces.widget.TabView);
    if (tab !== undefined) {
      const firstError = tab.jq.find(".ui-tabs-panels .ui-state-error").first();
      if (firstError.length > 0) {
        const index = firstError.closest(".ui-tabs-panel").index();
        if (index >= 0) {
          tab.select(index);
        }
      }
    }
  });
}

/**
 * Callback function that may be passed to PrimeFaces.monitorDownload(onStartDownload, onStopDownload).
 * Hides the loading dialog.
 */
export function onStopDownload(): void {
  enablePromptBeforeExit();
  const dialog = getWidgetByVar("downloadDialog", PrimeFaces.widget.Dialog);
  const framebus = getFramebusHandlerInstance();
  if (dialog !== undefined) {
    dialog.hide();
  } else if (isInstanceLike(framebus, FrameBusChildHandler)) {
    framebus.emitDownloadDialog("hide");
  }
}

/**
 * Called at the beginning of a remote command or server-side AJAX listener that should
 * result in a new window or tab being opened with a particular URL. Opens a new window or tab
 * as soon as the command. If the new window or tab were opened only once the AJAX request completes,
 * that gets blocked by popup blockers. If this method is called as a listener for a click event,
 * the new tab or window should not be blocked by popup blockers.
 *
 * Only once the remote command completes, the location of the newly opened window is set to the new URL.
 *
 * This is necessary in case the URL only becomes available once the AJAX request completes. For example,
 * we first need to create new form before we can redirect the user to the designer page.
 *
 * The server-side AJAX listener must call the method `remoteCommandNewTabComplete` via
 * `Primefaces.current().executeScript` and pass in the new URL for the current window and
 * the newly opened window.
 */
export function remoteCommandNewTabStart(): void {
  RemoteCommandWindow = window.open(encodeURL(LoadingPage, true), "_blank") ?? undefined;
}

/**
 * Called once the remote command has completed successfully. On the server side,
 * this function should be called via `Primefaces.current().executeScript`.
 * @param href URL to be opened in the new window or tab.
 * @param location New URL for the current window or tab. May be `undefined`, in
 * which case the location of the current window or tab is not changed.
 * @see remoteCommandNewTabStart
 */
export function remoteCommandNewTabComplete(href: string, location?: string): void {
  const remoteWindow = RemoteCommandWindow;
  RemoteCommandWindow = undefined;
  if (remoteWindow === undefined) {
    console.warn("No open window found");
    return;
  }
  if (typeof href !== "string") {
    console.warn("no link to open was returned");
    remoteWindow.close();
  } else {
    setTimeout(() => {
      remoteWindow.location.replace(href);
      if (notEmpty(location)) {
        doRedirect(location);
      }
    }, 500);
  }
}

/**
 * Called once the remote command has completed abnormally. On the server side,
 * this function should be called via `Primefaces.current().executeScript`.
 */
export function remoteCommandNewTabError(): void {
  if (RemoteCommandWindow === undefined) {
    console.warn("No open window found");
  } else {
    RemoteCommandWindow.close();
    RemoteCommandWindow = undefined;
  }
}

/**
 * For the serenity menu (to the left). Sets the active menu item to the given
 * view ID. Called once on page load so that the menu highlights the correct
 * item.
 * @param viewId The view ID of the menu item.
 */
export function setActiveMenuItem(viewId: string): void {
  const menuWidget = getWidgetByVar("sidebarMenuVar", PrimeFaces.widget.Serenity);
  if (menuWidget !== undefined) {
    const menuItem = jQuery(`.menu-id-${jQuery.escapeSelector(viewId)}`).parent();
    const parent = menuItem.parent().parent();
    const isParent = parent[0]?.tagName === "LI";
    menuWidget.deactivateItems(menuItem.siblings(), true);
    if (isParent !== undefined && isParent) {
      menuWidget.activate(parent);
    }
    menuWidget.activate(menuItem);
  }
}

export function showUnsavedChanges(): void {
  const $saveButton = $byId("actionbarForm:saveButton").add(".fc-save-button");
  $saveButton.addClass("fc-unsaved");
  jQuery(".context-actionbar").addClass("fc-unsaved");
}

/**
 * @param askBeforeExit Whether to ask the user for confirmation when the
 * attempt to close the current page.
 * @param message Message to show to the user. Note that while this may be
 * required by older browsers, newer browsers do not require this and in fact do
 * not even allow a message to be displayed.
 */
export function setPageExitPrompt(askBeforeExit: boolean, message?: string): void {
  askBeforeExitState.ask = askBeforeExit;
  if (askBeforeExit) {
    askBeforeExitState.message = isEmpty(message) ? "Are you certain you wish to exit this page?" : message;
  } else {
    askBeforeExitState.message = "";
  }
}

/**
 * Should be called on document ready. Initializes the page exit prompt
 * {@link setPageExitPrompt} by adding the appropriate event listeners.
 */
export function initPageExitPrompt(): void {
  window.addEventListener("beforeunload", (event) => {
    if (askBeforeExitState.ask && askBeforeExitState.enabled) {
      event.preventDefault(); // according to the spec
      event.returnValue = askBeforeExitState.message; // required for older browsers
    } else {
      delete event.returnValue;
    }
  });
}

/**
 * Disables the prompt asking the user whether they want to leave the current
 * page, irrespective of whether {@link setPageExitPrompt} was set to `true` or
 * `false`. Normally used to disabled the prompot temporarily during a file
 * download.
 */
export function disablePromptBeforeExit(): void {
  askBeforeExitState.enabled = false;
}

/**
 * Enables the prompt asking the user whether they want to leave the current
 * page, when disabled previously via {@link disablePromptBeforeExit}.
 */
export function enablePromptBeforeExit(): void {
  askBeforeExitState.enabled = true;
}
